import { graphql } from 'react-relay';
import { useMemo } from 'react';
import { usePageContentId } from '@confluence/page-context';

import { normalizeContentLabelsData } from '../normalizers/normalizeContentLabelsData.ts';

import type {
	useContentLabelsDetailsPanelContentLabelDeleteMutation$data,
	useContentLabelsDetailsPanelContentLabelDeleteMutation$variables,
} from './__generated__/useContentLabelsDetailsPanelContentLabelDeleteMutation.graphql.ts';
import type {
	useContentLabelsDetailsPanelContentLabelsQuery$data,
	useContentLabelsDetailsPanelContentLabelsQuery$variables,
} from './__generated__/useContentLabelsDetailsPanelContentLabelsQuery.graphql.ts';
import type { RefetchSupportedType } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { MutationFunctionInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
const useContentLabelsDetailsPanelContentLabelsQuery = graphql`
	query useContentLabelsDetailsPanelContentLabelsQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			id
			labels(orderBy: { sortField: LABELLING_CREATIONDATE, direction: ASCENDING }) {
				count
				nodes {
					id
					label
					name
					prefix
				}
			}
		}
		content(id: $contentId, status: ["archived", "current", "draft"]) {
			nodes {
				id
				type
				operations {
					operation
					targetType
				}
			}
		}
	}
`;
const useContentLabelsDetailsPanelContentLabelDeleteMutation = graphql`
	mutation useContentLabelsDetailsPanelContentLabelDeleteMutation(
		$contentId: ID!
		$label: String!
	) {
		deleteLabel(input: { contentId: $contentId, label: $label }) {
			contentId
		}
	}
`;
export const useContentLabels = (): {
	data: {
		labels: (
			| {
					readonly id: string | null | undefined;
					readonly label: string | null | undefined;
					readonly name: string | null | undefined;
					readonly prefix: string | null | undefined;
			  }
			| null
			| undefined
		)[];
		hasEditPermissions: boolean;
	};
	handleDeleteLabelClick: MutationFunctionInterop<
		useContentLabelsDetailsPanelContentLabelDeleteMutation$data,
		useContentLabelsDetailsPanelContentLabelDeleteMutation$variables
	>;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	refetch: RefetchSupportedType<
		useContentLabelsDetailsPanelContentLabelsQuery$data,
		useContentLabelsDetailsPanelContentLabelsQuery$variables
	>;
} => {
	const [contentId = ''] = usePageContentId();
	const { data, loading, error, refetch } = useQueryInterop<
		useContentLabelsDetailsPanelContentLabelsQuery$data,
		useContentLabelsDetailsPanelContentLabelsQuery$variables
	>(useContentLabelsDetailsPanelContentLabelsQuery, {
		variables: {
			contentId,
		},
		skip: !contentId,
	});
	const [handleDeleteLabelClick] = useMutationInterop<
		useContentLabelsDetailsPanelContentLabelDeleteMutation$data,
		useContentLabelsDetailsPanelContentLabelDeleteMutation$variables
	>(useContentLabelsDetailsPanelContentLabelDeleteMutation);

	const normalizedData = useMemo(() => normalizeContentLabelsData(data), [data]);

	return {
		data: normalizedData,
		handleDeleteLabelClick,
		loading,
		error,
		refetch,
	};
};
